/**
 * Motif cart guard — keeps Motif Studio projects and regular Mimeo products
 * from mixing in one cart.
 *
 * A Studio line is a main line marked `_builder: studio` or carrying
 * `_motifFileId` (the same rule as isStudioCartLine in assets/my-projects.js
 * and assets/cart-checkout-property-transform.js), or an ADDPAGE sub-line.
 * photos-client-api's buildAddPageLineAttributes stamps a sub-line with only
 * `_isMainPart`/`_lineItemType`/`_source`, so it is recognised through its
 * parent_key, or by `_lineItemType: sub` with a Motif `_source` when it
 * arrives flat. Without that, an ordinary Studio cart with additional pages
 * looks "mixed" to this guard and it offers to delete the shopper's own
 * add-page lines.
 *
 * Enforcement points:
 *  - Pre-add gate (ensureCanAddOther, wired into assets/product-form.js):
 *    theme-controlled adds (product form, quick-add) are blocked before they
 *    reach /cart/add if the cart already holds a Studio project — the
 *    conflicting set is removed only after the shopper confirms, so nothing
 *    is ever added then removed on this path.
 *  - Server-rendered veil + pre-opened modal (layout/theme.liquid,
 *    sections/main-cart-items.liquid): Mediaclip's full-page /cart/add
 *    navigation and Motif's own /cart/add permalink can't run theme JS
 *    before posting, so those adds land on the cart page with the item
 *    already in place. Liquid detects the mixed cart at render time and
 *    paints the veil plus an open dialog on first paint, before this
 *    deferred script even runs, so the shopper never sees the mixed listing.
 *  - Cart-page auto-resolve + checkout-click backstop (below): catch every
 *    other case — races, direct API adds (quick-add-bulk, quick-order-list),
 *    a mixed cart built in a second tab.
 *
 * Resolution: the newest set is the one whose lines are new, or grew, since
 * the cart was last seen unmixed in this browser (a re-add of an existing
 * product bumps its line in place rather than appending) — but only when
 * exactly one set changed. Otherwise the order is unknowable and the FIRST
 * line's type decides (/cart.js lists the most recently added line first,
 * checked live on the default and /en-de/ storefronts and for Studio adds);
 * the dialog copy names exactly what each button removes either way. "Proceed" removes the older
 * conflicting set, "Cancel" keeps it and removes (or blocks) what was just
 * added. Escape or the backdrop only dismiss the dialog; with no dialog
 * markup nothing is removed.
 */
(function () {
  'use strict';

  var pendingDecision = null;
  var CHECKOUT_SELECTOR = '#checkout, #CartDrawer-Checkout, button[name="checkout"]';
  var SNAPSHOT_KEY = 'motifCartGuardSnapshot';
  // A hung /cart.js or /cart/update.js must not hold the veil forever.
  var FETCH_TIMEOUT_MS = 8000;

  function cartJsUrl() {
    var base = (window.routes && window.routes.cart_url) || '/cart';
    return base + '.js';
  }

  function isCartPage() {
    var cartPath = (window.routes && window.routes.cart_url) || '/cart';
    return window.location.pathname === cartPath || window.location.pathname.indexOf(cartPath + '/') === 0;
  }

  function isStudioMainLine(item) {
    var props = item && item.properties;
    if (!props) return false;
    return String(props._builder || '').toLowerCase() === 'studio' || Boolean(props._motifFileId);
  }

  // An ADDPAGE sub-line that arrived without a parent link. Its `_source` is
  // `studio` or a Motif platform (`Motif-Web`, `Motif-iOS`, …).
  function isFlatStudioSubLine(item) {
    var props = item && item.properties;
    if (!props) return false;
    var source = String(props._source || '').toLowerCase();
    return String(props._lineItemType || '').toLowerCase() === 'sub' &&
      (source === 'studio' || source.indexOf('motif') === 0);
  }

  // Shopify reports a bundled child line's parent either flat (parent_key) or
  // nested (parent_relationship.parent_key) depending on the endpoint — same
  // pair assets/cart-checkout-property-transform.js reads.
  function parentLineKey(item) {
    return (item && (item.parent_key || (item.parent_relationship && item.parent_relationship.parent_key))) || '';
  }

  // Fetches JSON, racing a timer so a hung request settles even without
  // AbortController; a late response is ignored.
  function fetchJson(url, options) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer;
    var timeout = new Promise(function (_, reject) {
      timer = setTimeout(function () {
        if (controller) controller.abort();
        reject(new Error('Cart request timed out: ' + url));
      }, FETCH_TIMEOUT_MS);
    });
    var init = options || {};
    init.signal = controller ? controller.signal : undefined;
    var request = fetch(url, init).then(function (response) {
      if (!response.ok) throw new Error('Cart request failed: ' + response.status);
      return response.json();
    });
    // then(ok, err) rather than finally(): older WebViews lack finally.
    return Promise.race([request, timeout]).then(
      function (cart) {
        clearTimeout(timer);
        return cart;
      },
      function (error) {
        clearTimeout(timer);
        throw error;
      }
    );
  }

  function fetchCart() {
    return fetchJson(cartJsUrl(), { headers: { Accept: 'application/json' } });
  }

  // The snapshot is kept in localStorage so a cart opened in a new tab or an
  // in-app browser still sees it. Only a snapshot of this same cart counts: a
  // replaced cart (logout, shared checkout) has a new token, and the theme's
  // clear paths call clearSnapshot(), since Shopify reuses line keys when the
  // same item is added again.
  function readSnapshot(cart) {
    try {
      var saved = JSON.parse(window.localStorage.getItem(SNAPSHOT_KEY) || 'null');
      if (!saved || !saved.token || saved.token !== cart.token) return null;
      return saved.quantities || null;
    } catch (e) {
      return null;
    }
  }

  // Remembers an unmixed cart's line quantities for the next recency check.
  function saveSnapshot(cart) {
    try {
      var quantities = {};
      (cart.items || []).forEach(function (item) {
        quantities[item.key] = item.quantity;
      });
      window.localStorage.setItem(
        SNAPSHOT_KEY,
        JSON.stringify({ token: cart.token || '', quantities: quantities })
      );
    } catch (e) {
      /* recency falls back to the first (newest) line */
    }
  }

  function clearSnapshot() {
    try {
      window.localStorage.removeItem(SNAPSHOT_KEY);
    } catch (e) {
      /* nothing stored */
    }
  }

  // Refreshes the snapshot from a cart the caller already has, when unmixed.
  function rememberIfUnmixed(cart) {
    var parts = partitionCart(cart);
    if (!parts.studio.length || !parts.other.length) saveSnapshot(cart);
  }

  // A Studio line is a Studio main line or an ADDPAGE sub-line, nested under
  // one or flat (see the header note).
  function partitionCart(cart) {
    var items = cart.items || [];
    var studioKeys = {};
    items.forEach(function (item) {
      if (isStudioMainLine(item)) studioKeys[item.key] = true;
    });

    var studio = [];
    var other = [];
    items.forEach(function (item) {
      var isStudio = isStudioMainLine(item) || !!studioKeys[parentLineKey(item)] || isFlatStudioSubLine(item);
      (isStudio ? studio : other).push(item);
    });
    return { studio: studio, other: other };
  }

  // `previous` is the snapshot of the last unmixed cart ({key: quantity}).
  // When exactly one set has new or grown lines since then, that set is the
  // newest. When both changed their order is unknowable — any guess inverts
  // once the snapshot is stale — so the first line decides (/cart.js lists
  // the most recently added line first).
  function newestSetIsStudio(cart, parts, previous) {
    var items = cart.items || [];
    if (previous && typeof previous === 'object') {
      var grew = function (item) {
        return !(item.key in previous) || item.quantity > previous[item.key];
      };
      var studioGrew = parts.studio.some(grew);
      var otherGrew = parts.other.some(grew);
      if (studioGrew !== otherGrew) return studioGrew;
    }
    var newest = items[0];
    if (!newest) return false;
    return parts.studio.indexOf(newest) !== -1;
  }

  function getModal() {
    return document.getElementById('MotifCartGuardModal');
  }

  /**
   * Shows the Proceed/Cancel confirm dialog for the given variant
   * ('studio-newest', 'other-newest', or 'pre-add'). Resolves 'proceed'
   * (confirm — always means "keep/allow the newest thing, remove the
   * conflicting set"), 'cancel' (the Cancel button — keep the older set) or
   * 'dismiss' (Escape or the backdrop — change nothing). Reuses one pending
   * promise if the dialog is already open so a double-submit or a second
   * trigger cannot stack dialogs.
   *
   * If the dialog is already open because layout/theme.liquid server-
   * rendered it (mixed cart detected before this script ran), this skips
   * the open transition and just re-syncs the visible variant, attaches
   * listeners, and focuses the cancel button — avoiding a visible
   * close-then-reopen flicker.
   */
  function showConflictModal(variant) {
    if (pendingDecision) return pendingDecision;

    var modal = getModal();
    if (!modal) {
      // Markup missing (snippet not rendered): nothing can be confirmed, so
      // nothing may be removed.
      console.warn('MotifCartGuard: modal markup missing; keeping cart as-is');
      return Promise.resolve('unavailable');
    }

    var alreadyOpen = modal.classList.contains('motif-cart-guard--open') && !modal.hasAttribute('hidden');

    var variants = modal.querySelectorAll('[data-motif-guard-variant]');
    for (var i = 0; i < variants.length; i++) {
      variants[i].hidden = variants[i].getAttribute('data-motif-guard-variant') !== variant;
    }
    modal.setAttribute('aria-labelledby', 'MotifCartGuardTitle-' + variant);
    modal.setAttribute('aria-describedby', 'MotifCartGuardMessage-' + variant);

    pendingDecision = new Promise(function (resolve) {
      var previouslyFocused = document.activeElement;

      function settle(result) {
        pendingDecision = null;
        modal.setAttribute('hidden', '');
        modal.classList.remove('motif-cart-guard--open');
        document.removeEventListener('keydown', onKeydown, true);
        modal.removeEventListener('click', onClick);
        if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
          previouslyFocused.focus();
        }
        resolve(result);
      }

      function onKeydown(event) {
        if (modal.classList.contains('motif-cart-guard--busy')) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          settle('dismiss');
        }
      }

      function onClick(event) {
        if (modal.classList.contains('motif-cart-guard--busy')) return;
        var target = event.target;
        if (!target || !target.closest) return;
        if (target.closest('[data-motif-guard-confirm]')) {
          settle('proceed');
        } else if (target.closest('[data-motif-guard-cancel]')) {
          settle('cancel');
        } else if (target.closest('[data-motif-guard-dismiss]')) {
          settle('dismiss');
        }
      }

      modal.addEventListener('click', onClick);
      document.addEventListener('keydown', onKeydown, true);

      if (!alreadyOpen) {
        modal.removeAttribute('hidden');
        // Force a reflow so the open transition animates instead of snapping.
        void modal.offsetWidth;
        modal.classList.add('motif-cart-guard--open');
      }

      var cancelButton = modal.querySelector(
        '[data-motif-guard-variant="' + variant + '"] [data-motif-guard-cancel]'
      );
      if (cancelButton && typeof cancelButton.focus === 'function') cancelButton.focus();
    });

    return pendingDecision;
  }

  function removeSet(items) {
    var updates = {};
    items.forEach(function (item) {
      updates[item.key] = 0;
    });
    if (!Object.keys(updates).length) return Promise.resolve();
    var updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
    return fetchJson(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    });
  }

  /**
   * Checks whether the cart mixes Studio and non-Studio lines and, if so,
   * resolves the conflict via the confirm dialog, removing the losing set.
   * Exposed on window.MotifCartGuard for the cart-page auto-run and the
   * checkout-click backstop below.
   *
   * Resolves {status: 'clean'} when the cart was never mixed,
   * {status: 'resolved'} once the losing set has been removed,
   * {status: 'dismissed'} when the shopper closed the dialog without choosing,
   * or {status: 'unresolved'} when no dialog could be shown or the removal
   * failed. Fails open on /cart.js read errors (warn + 'clean') — this is a
   * mixing safeguard, not a hard gate, and a transient read failure should not
   * block checkout. A failed or hung /cart/update.js removal starts a reload
   * and settles 'unresolved', so the caller can reveal the cart even if the
   * reload itself stalls; the checkout backstop still stops a mixed cart.
   */
  function ensureCartNotMixed() {
    return fetchCart()
      .then(function (cart) {
        var parts = partitionCart(cart);
        if (!parts.studio.length || !parts.other.length) {
          saveSnapshot(cart);
          return { status: 'clean' };
        }

        var newestIsStudio = newestSetIsStudio(cart, parts, readSnapshot(cart));
        var variant = newestIsStudio ? 'studio-newest' : 'other-newest';
        return showConflictModal(variant).then(function (decision) {
          if (decision === 'unavailable') return { status: 'unresolved' };
          if (decision === 'dismiss') return { status: 'dismissed' };
          var keepNewest = decision === 'proceed';
          var losingSet = keepNewest === newestIsStudio ? parts.other : parts.studio;

          var modal = getModal();
          if (modal) modal.classList.add('motif-cart-guard--busy');

          return removeSet(losingSet).then(
            function () {
              if (modal) modal.classList.remove('motif-cart-guard--busy');
              return { status: 'resolved' };
            },
            function (error) {
              console.warn('MotifCartGuard: cart update failed, reloading', error);
              if (modal) modal.classList.remove('motif-cart-guard--busy');
              window.location.reload();
              return { status: 'unresolved' };
            }
          );
        });
      })
      .catch(function (error) {
        console.warn('MotifCartGuard: cart read failed, allowing', error);
        return { status: 'clean' };
      });
  }

  /**
   * Pre-add gate for theme-controlled adds (assets/product-form.js). Resolves
   * true if it's safe to proceed with the add (cart holds no Studio project,
   * or the shopper confirmed removing it), false if the add should be
   * aborted (shopper cancelled, or the removal failed — never leave the
   * caller to add on top of an unresolved conflict). Fails open (true) on a
   * /cart.js read error since the cart-page/checkout backstops still cover a
   * mixed cart that slips through.
   */
  function ensureCanAddOther() {
    return fetchCart()
      .then(function (cart) {
        var parts = partitionCart(cart);
        if (!parts.studio.length) return true;

        return showConflictModal('pre-add').then(function (decision) {
          // No dialog: never add on top of a Studio cart unconfirmed.
          if (decision !== 'proceed') return false;

          var modal = getModal();
          if (modal) modal.classList.add('motif-cart-guard--busy');

          return removeSet(parts.studio).then(
            function () {
              if (modal) modal.classList.remove('motif-cart-guard--busy');
              return true;
            },
            function (error) {
              console.warn('MotifCartGuard: cart update failed, blocking add', error);
              if (modal) modal.classList.remove('motif-cart-guard--busy');
              return false;
            }
          );
        });
      })
      .catch(function (error) {
        console.warn('MotifCartGuard: cart read failed, allowing add', error);
        return true;
      });
  }

  // Removes the server-rendered veil (sections/main-cart-items.liquid) once
  // the cart is confirmed not mixed. Safe to call even when no veil was
  // rendered. Also closes a server-opened modal that JS never had to open
  // itself (e.g. another tab resolved the conflict before this script ran).
  function revealCart() {
    var veil = document.getElementById('motif-cart-guard-veil');
    if (veil) veil.remove();

    var modal = getModal();
    if (modal && modal.classList.contains('motif-cart-guard--open') && !pendingDecision) {
      modal.setAttribute('hidden', '');
      modal.classList.remove('motif-cart-guard--open');
    }
  }

  // The cart page's own error region (sections/main-cart-items.liquid).
  function showCartError() {
    var region = document.getElementById('cart-errors');
    var message = window.cartStrings && window.cartStrings.error;
    if (region && message) {
      region.textContent = message;
    } else {
      console.warn('MotifCartGuard: mixed cart could not be resolved; checkout blocked');
    }
  }

  window.MotifCartGuard = {
    ensureCartNotMixed: ensureCartNotMixed,
    ensureCanAddOther: ensureCanAddOther,
    // The one place the snapshot key lives; the theme's cart-clear paths call it.
    clearSnapshot: clearSnapshot,
    // Pure cart-shape decisions, exposed for tests/motif-cart-guard.test.js —
    // the two rules most likely to drift (what counts as a Studio line, and
    // which end of cart.items is the newest add).
    partitionCart: partitionCart,
    newestSetIsStudio: newestSetIsStudio,
  };

  function runCartPageAutoResolve() {
    // Signals the veil's inline safety timer (sections/main-cart-items.liquid)
    // that guard JS is alive and will resolve any mixed cart itself, so the
    // timer's self-heal removal never races an in-progress resolution.
    window.__motifGuardVeilOwned = true;

    // A sign-out landing (layout/theme.liquid, ?motif_signed_out=1) empties
    // the cart through its serialized logout clear; a guard removal racing it
    // could commit after the clear verified empty and refill the cart for the
    // next account. Hand over the cart as-is, like the duplicate merge does.
    if (new URLSearchParams(window.location.search || '').get('motif_signed_out') === '1') {
      revealCart();
      return;
    }

    var mergeWait = window.__motifCartMergePromise || Promise.resolve('done');
    mergeWait.then(function (mergeResult) {
      // A 'reloading' merge is about to replace this page; the next load
      // re-runs this check against the merged cart.
      if (mergeResult === 'reloading') return;
      ensureCartNotMixed().then(function (result) {
        if (result.status === 'resolved') {
          window.location.reload();
          return;
        }
        revealCart();
      });
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    if (isCartPage()) runCartPageAutoResolve();
    // Keep the recency snapshot current after every theme cart change (e.g. a
    // quantity bump), so "both sets grew" only happens for changes made
    // outside the theme, whose order cannot be known.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        // Dawn's change/update events carry the resulting cart; fetch only
        // when an event does not.
        var cart = event && event.cartData;
        if (cart && cart.token && Array.isArray(cart.items)) {
          rememberIfUnmixed(cart);
          return;
        }
        fetchCart().then(rememberIfUnmixed, function () {});
      });
    }
  });

  // Checkout-click backstop: catches every path to checkout that isn't the
  // cart page itself (header cart link, cart drawer, notification popup).
  // Capture phase so this runs before other checkout-time listeners, e.g.
  // cart-checkout-property-transform.js's bubble-phase submit guard.
  document.addEventListener(
    'click',
    function (event) {
      var button = event.target && event.target.closest && event.target.closest(CHECKOUT_SELECTOR);
      if (!button) return;

      if (button.dataset.motifGuardChecked) {
        delete button.dataset.motifGuardChecked;
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      ensureCartNotMixed().then(function (result) {
        if (result.status === 'clean') {
          button.dataset.motifGuardChecked = 'true';
          button.click();
          return;
        }
        // Dismissed: the shopper closed the dialog; stay put.
        if (result.status === 'dismissed') return;
        // 'unresolved' on the cart page (no dialog, or the removal failed):
        // say why checkout did not open instead of doing nothing. Elsewhere,
        // the cart page renders the dialog.
        if (result.status === 'unresolved' && isCartPage()) {
          showCartError();
          return;
        }
        // 'resolved' — the cart just changed underneath the shopper. Never
        // send them straight to checkout after a destructive fix; show them
        // the resolved cart instead.
        if (isCartPage()) {
          window.location.reload();
        } else {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        }
      });
    },
    true
  );

})();
